<template>
  <BModal
    modal-name="bulkUpdateLeadStage"
    width="550px"
    height="400px"
    :delegate="modalDelegate"
  >
    <BHeightAdjuster>
      <BModalHeader
        center
        class="modal-header"
        :title="$t('leadStage.bulkUpdate.title')"
        @modal-close="handleClose"
      />
      <BModalBody class="modal-body">
        <BulkUpdateModalTarget
          :label="$t('leadStage.bulkUpdate.target')"
          :count="selectedCallTargetIds.length"
        />
        <BListItem>
          <template #header>
            <div>{{ $t('leadStage.bulkUpdate.selectLabel') }}</div>
          </template>
          <BSelect
            v-model="selectedLeadStageId"
            class="lead-stage-select"
            :items="leadStages"
            item-text="name"
            item-value="id"
            :placeholder="$t('leadStage.bulkUpdate.selectPlaceholder')"
            filterable
          >
            <template #optionTemplate="{ item }">
              <BLeadStagePreview :lead-stage="item" />
            </template>
          </BSelect>
        </BListItem>
        <BulkUpdateOverLimitMessage
          :visible="isOverLimit"
          :limit="BULK_UPDATE_LIMIT"
        />
      </BModalBody>
      <BulkUpdateModalFooter
        :loading="isLoading"
        :disabled="isUpdateDisabled"
        @cancel="handleClose"
        @submit="submitBulkUpdateLeadStage"
      />
    </BHeightAdjuster>
  </BModal>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { LeadStage } from '@/api/openapi';
import { CallTargetApiService } from '@/api/user/resources/call_target';
import BulkUpdateModalFooter from '@/components/organisms/user/leadList/BulkUpdateModalFooter.vue';
import BulkUpdateModalTarget from '@/components/organisms/user/leadList/BulkUpdateModalTarget.vue';
import BulkUpdateOverLimitMessage from '@/components/organisms/user/leadList/BulkUpdateOverLimitMessage.vue';
import { useConfirm } from '@/composable/message-dialog';
import { useWait } from '@/composable/vue-wait';
import { KEY_OF_MODAL, KEY_OF_TOAST } from '@/injection-keys';
import { key } from '@/store/index';

type TProps = {
  selectedCallTargetIds: number[];
};

const props = defineProps<TProps>();

const MODAL_NAME = 'bulkUpdateLeadStage';
const BULK_UPDATE_LIMIT = 100;
const i18n = useI18n();
const store = useStore(key);
const modal = inject(KEY_OF_MODAL);
const toast = inject(KEY_OF_TOAST);
const { wait, doActionWithWait } = useWait();
const { confirm } = useConfirm();
const api = new CallTargetApiService();

const selectedLeadStageId = ref<number | null>(null);

const leadStages = computed<LeadStage[]>(() => store.getters['user/leadStages'] ?? []);

onMounted(() => {
  store.dispatch('user/getLeadStagesAction');
});

const isOverLimit = computed<boolean>(() => props.selectedCallTargetIds.length > BULK_UPDATE_LIMIT);
const isUpdateDisabled = computed<boolean>(() =>
  props.selectedCallTargetIds.length === 0 || !selectedLeadStageId.value || isOverLimit.value,
);
const isLoading = computed<boolean>(() => wait.is(['bulkUpdateLeadStageWait']));

const modalDelegate = {
  beforeClose: () => {
    selectedLeadStageId.value = null;
  },
};

const handleClose = () => {
  modal.hide(MODAL_NAME);
};

const selectedLeadStageName = computed<string>(() =>
  leadStages.value.find((leadStage) => leadStage.id === selectedLeadStageId.value)?.name ?? '',
);

const submitBulkUpdateLeadStage = async () => {
  const count = props.selectedCallTargetIds.length;
  const confirmed = await confirm({
    message: i18n.t('leadStage.bulkUpdate.confirm', { count, leadStage: selectedLeadStageName.value }),
  });
  if (!confirmed) return;

  let succeeded = false;
  await doActionWithWait('bulkUpdateLeadStageWait', async () => {
    try {
      const response = await api.bulkUpdateLeadStage({
        request: {
          bulkLeadStageBody: {
            callTargetIds: props.selectedCallTargetIds,
            leadStageId: selectedLeadStageId.value,
          },
        },
      });
      succeeded = true;
      toast.show(i18n.t('leadStage.bulkUpdate.done', { count: response.data.updatedCount }));
    } catch {
      toast.show({ message: i18n.t('leadStage.bulkUpdate.failed'), type: 'error' });
    }
  });
  if (!succeeded) return;

  handleClose();
  await getCallList();
};

const getCallList = async () => {
  // UX改善のため、データ再読込時に全体ローディングしないようになっている
  toast.show({ message: i18n.t('callList.update.loading'), type: 'info' });
  await doActionWithWait('getCallTargetsBackgroundWait', async () => {
    await store.dispatch('user/getCallTargetsAction');
  });
  toast.show({ message: i18n.t('callList.update.done'), type: 'success' });
};
</script>

<style lang="scss" scoped>
  .modal-body {
    padding: $basespace-600;
  }

  .lead-stage-select {
    width: 100%;
  }

  .el-select-dropdown__item {
    // NOTE: リードステージ名が長い場合にドロップダウンの幅が広がってしまうため上限を設ける
    .lead-stage-preview {
      max-width: 430px;
    }
  }
</style>
